import React from 'react';
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { ExternalLink } from 'lucide-react';

const GATE1_TEST_LABELS = {
  repeatability: 'Repeatability',
  impact_magnitude: 'Impact Magnitude',
  causal_clarity: 'Causal Clarity',
  breadth: 'Breadth',
  escalation_trend: 'Escalation Trend',
  strategic_relevance: 'Strategic Relevance',
};

const GATE2_TEST_LABELS = {
  time_dependency: 'Time Dependency',
  capability_gap: 'Capability Gap',
  structural_depth: 'Structural Depth',
  reversibility: 'Reversibility',
  dependency_load: 'Dependency Load',
  external_force: 'External Force',
};

function InfoRow({ label, value }) {
  if (!value && value !== 0 && value !== false) return null;
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest w-36 shrink-0 pt-0.5">{label}</span>
      <span className="text-xs text-slate-200 flex-1 break-words">{String(value)}</span>
    </div>
  );
}

function parseJsonStringOrArray(items) {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'string') {
    try { const parsed = JSON.parse(items); return Array.isArray(parsed) ? parsed : [parsed]; } catch { return []; }
  }
  return [];
}

const COLOR_MAP = {
  sky: { dot: 'bg-sky-400', id: 'text-sky-400/70', border: 'border-sky-500/10', bg: 'bg-sky-500/5' },
  amber: { dot: 'bg-amber-400', id: 'text-amber-400/70', border: 'border-amber-500/10', bg: 'bg-amber-500/5' },
  emerald: { dot: 'bg-emerald-400', id: 'text-emerald-400/70', border: 'border-emerald-500/10', bg: 'bg-emerald-500/5' },
};

function getLinkedUrl(idKey, id) {
  if (idKey === 'issue_id') return createPageUrl('issues') + '?tab=issues&highlight=' + id;
  if (idKey === 'policy_id') return createPageUrl('issues') + '?tab=policies&highlight=' + id;
  if (idKey === 'capability_id') return createPageUrl('issues') + '?tab=capabilities&highlight=' + id;
  return null;
}

function RelatedItemsList({ label, items, idKey, color }) {
  const parsed = parseJsonStringOrArray(items);
  if (parsed.length === 0) return null;
  const c = COLOR_MAP[color] || COLOR_MAP.sky;
  return (
    <div className="py-2.5">
      <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest block mb-2">{label}</span>
      <div className="space-y-2">
        {parsed.map((item, i) => {
          const isObj = typeof item === 'object' && item !== null;
          const id = isObj ? item[idKey] : null;
          const relevance = isObj ? item.relevance : null;
          const title = isObj ? (item.title || item.issue_title || item.policy_title || item.capability_title) : null;
          const displayText = relevance || title || (isObj ? JSON.stringify(item) : String(item));
          const linkUrl = id ? getLinkedUrl(idKey, id) : null;
          return (
            <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${c.border} ${c.bg}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${c.dot}`} />
              <div className="flex-1 min-w-0">
                {id && (
                  linkUrl ? (
                    <Link to={linkUrl} className={`text-[10px] font-mono ${c.id} hover:underline inline-flex items-center gap-1 mb-0.5`}>
                      {id} <ExternalLink className="w-2.5 h-2.5" />
                    </Link>
                  ) : (
                    <span className={`text-[10px] font-mono ${c.id} block mb-0.5`}>{id}</span>
                  )
                )}
                <span className="text-sm text-slate-200 leading-relaxed">{displayText}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RichTextRow({ label, value }) {
  if (!value && value !== 0 && value !== false) return null;
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest w-36 shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-slate-200 flex-1 break-words leading-relaxed whitespace-pre-wrap">{String(value)}</span>
    </div>
  );
}

function TestGrid({ tests, labels }) {
  if (!tests) return <span className="text-xs text-slate-500 italic">No test data</span>;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      {Object.entries(labels).map(([key, label]) => {
        const val = tests[key];
        return (
          <div key={key} className="flex items-center gap-2 py-1">
            <span className={`w-3 h-3 rounded-full shrink-0 ${val ? 'bg-emerald-500' : 'bg-slate-700'}`} />
            <span className="text-xs text-slate-300">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4">
      <h4 className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-2 border-b border-white/[0.06] pb-1.5">{title}</h4>
      {children}
    </div>
  );
}

export default function IssueIntelPanel({ issue }) {
  const ai = issue.ai_enrichment || {};
  const formatDate = (d) => {
    if (!d) return null;
    try { return format(new Date(d), 'MMM d, yyyy h:mm a'); } catch { return d; }
  };

  return (
    <div className="mt-3 p-4 rounded-xl border border-indigo-500/15 bg-indigo-950/10 animate-in fade-in-0 slide-in-from-top-1 duration-200 max-h-[400px] overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-700/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-600/60">
      {/* Core Info */}
      <Section title="Core Information">
        <InfoRow label="Category" value={issue.category?.replace(/_/g, ' ')} />
        <InfoRow label="Source" value={issue.source?.replace(/_/g, ' ')} />
        <InfoRow label="Severity" value={issue.severity} />
        <InfoRow label="Frequency" value={issue.frequency_signal?.replace(/_/g, ' ')} />
        <InfoRow label="Observed Impact" value={issue.observed_impact} />
        <InfoRow label="Description" value={issue.description} />
        <InfoRow label="Reported By" value={issue.reported_by} />
        <InfoRow label="Reported At" value={formatDate(issue.reported_at)} />
        <InfoRow label="Status" value={issue.status} />
      </Section>

      {/* Gate 1 */}
      <Section title="Gate 1 — Signal Detection">
        <div className="flex items-center gap-3 mb-2">
          <Badge variant="outline" className={`text-[10px] font-mono font-bold ${
            issue.classification_gate1 === 'signal' ? 'text-emerald-400 border-emerald-500/30' :
            issue.classification_gate1 === 'noise' ? 'text-slate-400 border-slate-500/30' :
            'text-amber-400 border-amber-500/30'
          }`}>
            {(issue.classification_gate1 || 'pending').toUpperCase()}
          </Badge>
          <span className="text-xs text-slate-400">Score: <span className="text-white font-mono font-bold">{issue.gate1_score ?? '—'}</span>/6</span>
          {issue.gate1_classified_at && <span className="text-[10px] text-slate-500">{formatDate(issue.gate1_classified_at)}</span>}
        </div>
        <TestGrid tests={issue.gate1_tests} labels={GATE1_TEST_LABELS} />
      </Section>

      {/* Gate 2 */}
      <Section title="Gate 2 — Density Classification">
        <div className="flex items-center gap-3 mb-2">
          <Badge variant="outline" className={`text-[10px] font-mono font-bold ${
            issue.classification_gate2 === 'rock' ? 'text-stone-400 border-stone-500/30' :
            issue.classification_gate2 === 'balloon' ? 'text-sky-400 border-sky-500/30' :
            'text-amber-400 border-amber-500/30'
          }`}>
            {issue.classification_gate2 === 'balloon' ? '🎈 BALLOON' : issue.classification_gate2 === 'rock' ? '🪨 ROCK' : 'PENDING'}
          </Badge>
          <span className="text-xs text-slate-400">Score: <span className="text-white font-mono font-bold">{issue.gate2_score ?? '—'}</span>/6</span>
          {issue.gate2_classified_at && <span className="text-[10px] text-slate-500">{formatDate(issue.gate2_classified_at)}</span>}
        </div>
        <TestGrid tests={issue.gate2_tests} labels={GATE2_TEST_LABELS} />
      </Section>

      {/* Classification Metadata */}
      {(issue.classified_by || issue.dissent_notes) && (
        <Section title="Classification Metadata">
          <InfoRow label="Classified By" value={issue.classified_by} />
          <InfoRow label="Classified At" value={formatDate(issue.classified_at)} />
          <InfoRow label="Dissent Notes" value={issue.dissent_notes} />
        </Section>
      )}

      {/* AI Analysis */}
      {Object.keys(ai).length > 0 && (
        <Section title="AI Analysis">
          {ai.pattern_type && <InfoRow label="Pattern Type" value={ai.pattern_type} />}
          {ai.pattern_description && <InfoRow label="Pattern" value={ai.pattern_description} />}
          {ai.root_cause_hypothesis && <InfoRow label="Root Cause" value={ai.root_cause_hypothesis} />}
          {ai.recommended_approach && <RichTextRow label="Recommendation" value={ai.recommended_approach} />}
          {ai.similar_issues_count != null && <InfoRow label="Similar Issues" value={ai.similar_issues_count} />}
          {ai.estimated_resolution_time && <InfoRow label="Est. Resolution" value={ai.estimated_resolution_time} />}
          {ai.confidence_score != null && <InfoRow label="Confidence" value={`${Math.round(ai.confidence_score * 100)}%`} />}
          {/* Recommended actions list */}
          {ai.recommended_actions?.length > 0 && (
            <div className="py-1.5">
              <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest block mb-1.5">Recommended Actions</span>
              <ul className="space-y-1.5 ml-1">
                {ai.recommended_actions.map((action, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-indigo-400 font-bold text-xs mt-0.5 shrink-0">{i + 1}.</span>
                    <span className="text-sm text-slate-200 leading-relaxed">{typeof action === 'object' ? action.action || JSON.stringify(action) : action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Related issues list */}
          {ai.related_issues && (
            <RelatedItemsList label="Related Issues" items={ai.related_issues} idKey="issue_id" color="sky" />
          )}
          {/* Related policies list */}
          {ai.related_policies && (
            <RelatedItemsList label="Related Policies" items={ai.related_policies} idKey="policy_id" color="amber" />
          )}
          {/* Related capabilities list */}
          {ai.related_capabilities && (
            <RelatedItemsList label="Related Capabilities" items={ai.related_capabilities} idKey="capability_id" color="emerald" />
          )}
          {/* Render remaining ai fields (exclude gate prediction fields) */}
          {Object.entries(ai).filter(([k]) => !['pattern_type','pattern_description','root_cause_hypothesis','recommended_approach','similar_issues_count','estimated_resolution_time','confidence_score','recommended_actions','related_issues','related_policies','related_capabilities','gate1_prediction','gate1_confidence','gate1_test_predictions','gate2_prediction','gate2_confidence','gate2_test_predictions'].includes(k)).map(([k, v]) => (
            <RichTextRow key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v, null, 2) : v} />
          ))}
          {issue.ai_enriched_at && <InfoRow label="Enriched At" value={formatDate(issue.ai_enriched_at)} />}
        </Section>
      )}

      {/* Resolution Info (if any) */}
      {(issue.resolution_owner || issue.decision || issue.decision_notes) && (
        <Section title="Resolution Details">
          <InfoRow label="Decision" value={issue.decision?.replace(/_/g, ' ')} />
          <InfoRow label="Decision By" value={issue.decision_made_by} />
          <InfoRow label="Decision At" value={formatDate(issue.decision_made_at)} />
          <InfoRow label="Decision Notes" value={issue.decision_notes} />
          <InfoRow label="Resolution Owner" value={issue.resolution_owner} />
          <InfoRow label="Timeline" value={issue.resolution_timeline} />
          <InfoRow label="Success Criteria" value={issue.resolution_success_criteria} />
        </Section>
      )}

      {/* Tags */}
      {(issue.tagged_officers?.length > 0 || issue.tagged_artifacts?.length > 0 || issue.tagged_policies?.length > 0 || issue.tagged_capabilities?.length > 0 || issue.tagged_rituals?.length > 0) && (
        <Section title="Tags">
          {issue.tagged_officers?.length > 0 && <InfoRow label="Officers" value={issue.tagged_officers.join(', ')} />}
          {issue.tagged_artifacts?.length > 0 && <InfoRow label="Artifacts" value={issue.tagged_artifacts.join(', ')} />}
          {issue.tagged_policies?.length > 0 && <InfoRow label="Policies" value={issue.tagged_policies.join(', ')} />}
          {issue.tagged_capabilities?.length > 0 && <InfoRow label="Capabilities" value={issue.tagged_capabilities.join(', ')} />}
          {issue.tagged_rituals?.length > 0 && <InfoRow label="Rituals" value={issue.tagged_rituals.join(', ')} />}
        </Section>
      )}

      {/* Re-review */}
      {issue.re_review_flagged && (
        <Section title="Re-Review">
          <InfoRow label="Flagged" value="Yes" />
          <InfoRow label="Reason" value={issue.re_review_reason} />
        </Section>
      )}
    </div>
  );
}